/* Catalog API helpers. */

import { api } from "./api";

export type Category = {
  id: number;
  name: string;
  slug: string;
  parent_id: number | null;
  position: number;
  is_active: boolean;
  product_count: number;
};

export type ProductImage = {
  id: number;
  image_url: string;
  thumb_url: string;
  alt_text: string;
  position: number;
  is_primary: boolean;
};

export type StockLevel = {
  warehouse_id: number;
  warehouse_name: string;
  quantity: number;
  low_stock_threshold: number;
  is_low_stock: boolean;
  is_out_of_stock: boolean;
};

/** «Статус наличия» codes (apps.catalog.models.Product.Availability). */
export type AvailabilityStatus = "" | "in_stock" | "on_order" | "unavailable" | "expected";
export const AVAILABILITY_STATUSES = ["in_stock", "on_order", "unavailable", "expected"] as const;

export type ProductListItem = {
  id: number;
  name: string;
  sku: string;
  ean: string;
  category_id: number | null;
  category_name: string | null;
  group_name: string | null;
  subgroup_name: string | null;
  price_eur: number;
  /** The client's own price — only on a list asked `for_client`, when set. */
  client_price_eur?: number | string | null;
  /** A client's discounted lots of the card (`offers_for`, 2026-09-28). */
  expiry_offers?: import("./suppliers").ExpiryOffer[];
  vat_rate: number;
  unit: string;
  packaging_qty: number;
  pallet_qty: number;
  min_order_qty: number;
  net_weight_kg: number | null;
  is_active: boolean;
  total_stock: number;
  /** The nearest best-before among lots on the shelf (Products list only). */
  nearest_expires_on?: string | null;
  primary_image_url: string | null;
  primary_thumb_url: string | null;
  primary_tile_url: string | null;
  manufacturer: string;
  supplier: string;
  supplier_record_id: number | null;
  canonical_state: "" | "linked" | "delisted";
  /** "" | sell_off — terminated-partnership leftovers kept for a final sale. */
  supply_state: "" | "sell_off";
  /** «Недоступна у поставщика» (spec 20260915 §2): why, since when, whether
   *  the owner has decided, and whether the supplier has brought it back. */
  supplier_gap_reason?: "" | "access_closed" | "delisted" | "link_ended";
  supplier_gap_since?: string | null;
  supplier_gap_decided_at?: string | null;
  supplier_back_at?: string | null;
  close_when_sold_out?: boolean;
  /** Public-page exposure (OE-A09): propagated copies start hidden. */
  public_visible: boolean;
  /** «Доступна к заказу» (2026-09-16): off = temporarily unavailable, greyed
   *  for clients and on «Цены и доступ», never on an order line. */
  is_orderable: boolean;
  /** «Статус наличия», set on Склад — shown to registered clients only. */
  availability_status: AvailabilityStatus;
  availability_expected_on: string | null;
  /** Our note to partner companies about this card (never public). */
  partner_note: string;
  /** The supplier's note as read through our linked copy of their card.
   *  List rows only — the detail payload doesn't carry it. */
  canonical_partner_note?: string;
  brand: string;
  customs_code: string;
  origin_country_id: number | null;
  origin_country_code: string | null;
  origin_country_name: string | null;
  gross_weight_kg: number | null;
  length_cm: number | null;
  width_cm: number | null;
  height_cm: number | null;
  // Full scalar set (the "All fields" list preset)
  description: string;
  shelf_life_days: number | null;
  storage_conditions: string;
  lead_time_days: number;
  has_export: boolean;
  incoterms: string;
  brand_ownership: string;
  composition: string;
  energy_kcal: number | null;
  energy_kj: number | null;
  fat_g: number | null;
  saturated_fat_g: number | null;
  carbs_g: number | null;
  sugars_g: number | null;
  protein_g: number | null;
  salt_g: number | null;
  egais_code: string;
  alcohol_pct: number | null;
  acetic_acid_pct: number | null;
  is_supplement: boolean;
  bicarbonates: number | null;
  sulfates: number | null;
  chlorides: number | null;
  calcium: number | null;
  magnesium: number | null;
  sodium: number | null;
  potassium: number | null;
  vitamin_c_mg: number | null;
  vitamin_d: number | null;
  min_remaining_shelf_life_pct: number | null;
  storage_temp_regime: string;
  humidity_max_pct: number | null;
  after_opening_conditions: string;
  after_opening_hours: number | null;
  needs_temp_control: boolean;
  packaging_languages: string[];
  has_deposit: boolean;
  // Accounting marks (2026-09-16); the deposit (DIO) mark is has_deposit.
  sugar_excise: boolean;
  is_alcohol: boolean;
  excise_stamp_range: string;
  excise_stamp_supplier: string;
  /** «Поставщик акцизных марок» from Настройки → Склад → Акцизный товар. */
  stamp_supplier_id: number | null;
  /** «Вид алкоголя» — a code of GET /meta/alcohol-types. */
  alcohol_type: string;
  is_coffee: boolean;
  display_box_length_cm: number | null;
  display_box_width_cm: number | null;
  display_box_height_cm: number | null;
  pcs_per_display_box: number | null;
  display_boxes_per_box: number | null;
  box_length_cm: number | null;
  box_width_cm: number | null;
  box_height_cm: number | null;
  pcs_per_box: number | null;
  boxes_per_layer: number | null;
  pallet_type_id: number | null;
  pallet_type_code: string | null;
  pallet_type_name: string | null;
  pallet_allowed_height_m: number | null;
  pallet_weight_kg: number | null;
  boxes_per_pallet: number | null;
  layers_per_pallet: number | null;
  pcs_per_pallet: number | null;
  total_pallet_gross_kg: number | null;
  /* The rest of the card, so every field edits in the list (2026-09-13). */
  volume_unit?: string;
  volume_value?: number | null;
  cargo_nature?: string;
  /** Products list rows only — a catalogue browse leaves them empty. */
  allergen_ids?: number[];
  standard_ids?: number[];
  label_icon_ids?: number[];
};

export type PackagingMaterial = {
  id: number;
  level: "unit" | "display_box" | "transport_box" | "pallet";
  position: number;
  slot_label: string;
  package_code_id: number | null;
  package_code: string | null;
  letter_code: string | null;
  pct_of_weight: number | null;
  weight_kg: number | null;
};

export type AllergenRef = { id: number; code: string; name: string };
export type StandardRef = { id: number; code: string; name: string };
export type LabelIconRef = { id: number; code: string; name: string };

export type ProductDetail = ProductListItem & {
  description: string;
  net_weight_kg: number | null;
  /** Liquid volume of one unit: "" | "l" | "ml" + the value in that unit. */
  volume_unit: string;
  volume_value: number | null;
  shelf_life_days: number | null;
  storage_conditions: string;
  lead_time_days: number;
  group_name: string | null;
  subgroup_name: string | null;
  // General info
  manufacturer: string;
  supplier: string;
  supplier_record_id: number | null;
  canonical_product_id: number | null;
  canonical_state: "" | "linked" | "delisted";
  supply_state: "" | "sell_off";
  /** Public-page exposure (OE-A09): propagated copies start hidden. */
  public_visible: boolean;
  /** «Доступна к заказу» (2026-09-16): off = temporarily unavailable, greyed
   *  for clients and on «Цены и доступ», never on an order line. */
  is_orderable: boolean;
  /** «Статус наличия», set on Склад — shown to registered clients only. */
  availability_status: AvailabilityStatus;
  availability_expected_on: string | null;
  partner_note: string;
  origin_country_id: number | null;
  origin_country_code: string | null;
  origin_country_name: string | null;
  customs_code: string;
  has_export: boolean;
  incoterms: string;
  // Description
  brand: string;
  brand_ownership: string;
  composition: string;
  // Nutrition
  energy_kcal: number | null;
  energy_kj: number | null;
  fat_g: number | null;
  saturated_fat_g: number | null;
  carbs_g: number | null;
  sugars_g: number | null;
  protein_g: number | null;
  salt_g: number | null;
  // Additional
  egais_code: string;
  alcohol_pct: number | null;
  acetic_acid_pct: number | null;
  is_supplement: boolean;
  // Minerals & vitamins
  bicarbonates: number | null;
  sulfates: number | null;
  chlorides: number | null;
  calcium: number | null;
  magnesium: number | null;
  sodium: number | null;
  potassium: number | null;
  vitamin_c_mg: number | null;
  vitamin_d: number | null;
  // Shelf life & storage
  min_remaining_shelf_life_pct: number | null;
  storage_temp_regime: string;
  humidity_max_pct: number | null;
  after_opening_conditions: string;
  after_opening_hours: number | null;
  needs_temp_control: boolean;
  // Label
  packaging_languages: string[];
  has_deposit: boolean;
  // Accounting marks (2026-09-16); the deposit (DIO) mark is has_deposit.
  sugar_excise: boolean;
  is_alcohol: boolean;
  excise_stamp_range: string;
  excise_stamp_supplier: string;
  /** «Поставщик акцизных марок» from Настройки → Склад → Акцизный товар. */
  stamp_supplier_id: number | null;
  /** «Вид алкоголя» — a code of GET /meta/alcohol-types. */
  alcohol_type: string;
  is_coffee: boolean;
  // Logistics · unit
  gross_weight_kg: number | null;
  length_cm: number | null;
  width_cm: number | null;
  height_cm: number | null;
  // Logistics · display box
  display_box_length_cm: number | null;
  display_box_width_cm: number | null;
  display_box_height_cm: number | null;
  pcs_per_display_box: number | null;
  display_boxes_per_box: number | null;
  // Logistics · transport box
  box_length_cm: number | null;
  box_width_cm: number | null;
  box_height_cm: number | null;
  pcs_per_box: number | null;
  boxes_per_layer: number | null;
  // Logistics · pallet
  pallet_type_id: number | null;
  pallet_type_code: string | null;
  pallet_type_name: string | null;
  pallet_allowed_height_m: number | null;
  pallet_weight_kg: number | null;
  boxes_per_pallet: number | null;
  layers_per_pallet: number | null;
  pcs_per_pallet: number | null;
  total_pallet_gross_kg: number | null;
  cargo_nature: string;
  // Nested
  packaging_materials: PackagingMaterial[];
  allergens: AllergenRef[];
  standards: StandardRef[];
  /** What this card answers, grouped by characteristic. */
  characteristics: { code: string; name: string; values: StandardRef[] }[];
  /** Sections ticked on the card — "wine", "mineral_water". */
  characteristic_sets: string[];
  label_icons: LabelIconRef[];
  /** The company's own «Учётные признаки» ticked on this card. */
  custom_marks: ProductMark[];
  images: ProductImage[];
  stock_levels: StockLevel[];
};

/** A fully-defaulted, unsaved product — backs the New-product screen, which
    reuses the detail page's editable spec form. Mirrors the create defaults on
    the backend (vat 21 %, unit g, qty 1, active). */
export function blankProductDetail(): ProductDetail {
  return {
    id: 0,
    name: "",
    sku: "",
    ean: "",
    category_id: null,
    category_name: null,
    group_name: null,
    subgroup_name: null,
    price_eur: 0,
    vat_rate: 21,
    unit: "g",
    packaging_qty: 1,
    pallet_qty: 1,
    min_order_qty: 1,
    net_weight_kg: null,
    volume_unit: "",
    volume_value: null,
    is_active: true,
    total_stock: 0,
    primary_image_url: null,
    primary_thumb_url: null,
    primary_tile_url: null,
    manufacturer: "",
    supplier: "",
    supplier_record_id: null,
    canonical_product_id: null,
    canonical_state: "",
    supply_state: "",
    public_visible: false,
    is_orderable: false,
    availability_status: "",
    availability_expected_on: null,
    partner_note: "",
    brand: "",
    customs_code: "",
    origin_country_id: null,
    origin_country_code: null,
    origin_country_name: null,
    gross_weight_kg: null,
    length_cm: null,
    width_cm: null,
    height_cm: null,
    description: "",
    shelf_life_days: null,
    storage_conditions: "",
    lead_time_days: 1,
    has_export: false,
    incoterms: "",
    brand_ownership: "own",
    composition: "",
    energy_kcal: null,
    energy_kj: null,
    fat_g: null,
    saturated_fat_g: null,
    carbs_g: null,
    sugars_g: null,
    protein_g: null,
    salt_g: null,
    egais_code: "",
    alcohol_pct: null,
    acetic_acid_pct: null,
    is_supplement: false,
    bicarbonates: null,
    sulfates: null,
    chlorides: null,
    calcium: null,
    magnesium: null,
    sodium: null,
    potassium: null,
    vitamin_c_mg: null,
    vitamin_d: null,
    min_remaining_shelf_life_pct: null,
    storage_temp_regime: "",
    humidity_max_pct: null,
    after_opening_conditions: "",
    after_opening_hours: null,
    needs_temp_control: false,
    packaging_languages: [],
    has_deposit: false,
    sugar_excise: false,
    is_alcohol: false,
    excise_stamp_range: "",
    excise_stamp_supplier: "",
    stamp_supplier_id: null,
    alcohol_type: "",
    is_coffee: false,
    display_box_length_cm: null,
    display_box_width_cm: null,
    display_box_height_cm: null,
    pcs_per_display_box: null,
    display_boxes_per_box: null,
    box_length_cm: null,
    box_width_cm: null,
    box_height_cm: null,
    pcs_per_box: null,
    boxes_per_layer: null,
    pallet_type_id: null,
    pallet_type_code: null,
    pallet_type_name: null,
    pallet_allowed_height_m: null,
    pallet_weight_kg: null,
    boxes_per_pallet: null,
    layers_per_pallet: null,
    pcs_per_pallet: null,
    total_pallet_gross_kg: null,
    cargo_nature: "",
    packaging_materials: [],
    allergens: [],
    standards: [],
    characteristics: [],
    characteristic_sets: [],
    label_icons: [],
    custom_marks: [],
    images: [],
    stock_levels: [],
  };
}

export type PaginatedProducts = {
  count: number;
  results: ProductListItem[];
  /** With `for_client`: "open" = only what the client may buy; "none_open" =
   *  nothing opened to it, so the whole catalogue; "" = not a client. */
  client_scope?: "" | "open" | "none_open";
};

export type CurrencyRate = {
  target_currency: string;
  rate: number;
  date: string;
};

export async function fetchCategories(slug: string): Promise<Category[]> {
  return api.get<Category[]>(`/t/${slug}/categories`);
}

export type CategoryInput = {
  name: string;
  slug: string;
  parent_id?: number | null;
  position?: number;
};

export async function createCategory(slug: string, data: CategoryInput): Promise<Category> {
  return api.post<Category>(`/t/${slug}/categories`, data);
}

export async function updateCategory(
  slug: string,
  id: number,
  data: Partial<CategoryInput>,
): Promise<Category> {
  return api.patch<Category>(`/t/${slug}/categories/${id}`, data);
}

/** Persist a new category order; index of each id becomes its position. */
export async function reorderCategories(
  slug: string,
  orderedIds: number[],
): Promise<Category[]> {
  return api.post<Category[]>(`/t/${slug}/categories/reorder`, { ordered_ids: orderedIds });
}

export type CategoryDeleteResult = { deleted: boolean; detached_products: number };

export async function deleteCategory(
  slug: string,
  id: number,
  detachProducts = false,
): Promise<CategoryDeleteResult> {
  const qs = detachProducts ? "?detach_products=true" : "";
  return api.delete<CategoryDeleteResult>(`/t/${slug}/categories/${id}${qs}`);
}

export type DashboardKpis = {
  total_orders: number;
  revenue_eur: number;
  active_products: number;
  linked_partners: number;
  pending_inbox: number;
  /** Direction-split metrics (OE-A08): sales = fulfilled, procurement = placed. */
  sales_orders: number;
  procurement_orders: number;
  procurement_spend_eur: number;
  suppliers_count: number;
  clients_count: number;
};

export async function fetchDashboardKpis(slug: string): Promise<DashboardKpis> {
  return api.get<DashboardKpis>(`/t/${slug}/dashboard/kpis`);
}

export async function fetchProducts(
  slug: string,
  params: Record<string, string | number | boolean | undefined> = {},
): Promise<PaginatedProducts> {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== "") qs.set(k, String(v));
  }
  const path = `/t/${slug}/products${qs.toString() ? "?" + qs : ""}`;
  return api.get<PaginatedProducts>(path);
}

/** Fetch every product (pages through the list endpoint). For pickers/selectors. */
export async function fetchAllProducts(slug: string): Promise<ProductListItem[]> {
  const out: ProductListItem[] = [];
  let page = 1;
  for (;;) {
    const res = await fetchProducts(slug, { page, order_by: "name" });
    out.push(...res.results);
    if (out.length >= res.count || res.results.length === 0) break;
    page += 1;
    if (page > 100) break; // safety cap
  }
  return out;
}

export async function fetchProduct(slug: string, id: number): Promise<ProductDetail> {
  return api.get<ProductDetail>(`/t/${slug}/products/${id}`);
}

/** Distinct supplier names for the tenant — populates the list's supplier filter. */
export async function fetchProductSuppliers(slug: string): Promise<string[]> {
  return api.get<string[]>(`/t/${slug}/product-suppliers`);
}

/** Create accepts the full editable spec (same shape as the patch payload),
    with `name` required — the New-product screen sends the whole draft. */
export type ProductCreateInput = ProductPatch & {
  name: string;
  packaging_languages?: string[];
  allergen_ids?: number[];
  standard_ids?: number[];
  label_icon_ids?: number[];
  packaging_materials?: Record<string, unknown>[];
  [key: string]: unknown;
};

export async function createProduct(
  slug: string,
  data: ProductCreateInput,
): Promise<ProductDetail> {
  return api.post<ProductDetail>(`/t/${slug}/products`, data);
}

export type ProductPatch = {
  name?: string;
  sku?: string;
  ean?: string;
  description?: string;
  category_id?: number | null;
  price_eur?: number;
  vat_rate?: number;
  unit?: string;
  packaging_qty?: number;
  min_order_qty?: number;
  pallet_qty?: number | null;
  net_weight_kg?: number | null;
  shelf_life_days?: number | null;
  storage_conditions?: string;
  lead_time_days?: number;
  is_active?: boolean;
  supplier?: string;
  public_visible?: boolean;
  is_orderable?: boolean;
  pcs_per_box?: number | null;
};

export async function updateProduct(
  slug: string,
  id: number,
  patch: ProductPatch,
): Promise<ProductDetail> {
  return api.patch<ProductDetail>(`/t/${slug}/products/${id}`, patch);
}

/** «На сайте» / «К заказу» on many cards at once — the header tick. */
export async function setProductFlags(
  slug: string,
  ids: number[],
  flags: { public_visible?: boolean; is_orderable?: boolean },
): Promise<{ updated: number }> {
  return api.post<{ updated: number }>(`/t/${slug}/products/flags`, { ids, ...flags });
}

export async function fetchCurrencyRates(): Promise<CurrencyRate[]> {
  return api.get<CurrencyRate[]>("/currency-rates");
}

/** Upload a photo for a product (multipart). The first image becomes primary. */
export async function uploadProductImage(
  slug: string,
  productId: number,
  file: File,
): Promise<ProductImage> {
  const form = new FormData();
  form.append("image", file);
  const res = await fetch(`/api/v1/t/${slug}/products/${productId}/images`, {
    method: "POST",
    body: form,
    credentials: "include",
  });
  if (!res.ok) {
    const body = await res.json().catch(() => null);
    throw new Error(body?.error?.message ?? "Upload failed");
  }
  return res.json();
}

export async function deleteProductImage(
  slug: string,
  productId: number,
  imageId: number,
): Promise<void> {
  await api.delete(`/t/${slug}/products/${productId}/images/${imageId}`);
}

/** Persist gallery order. The first id becomes the cover — reordering and
 *  picking a cover are the same gesture in the media manager. */
export async function reorderProductImages(
  slug: string,
  productId: number,
  imageIds: number[],
): Promise<ProductImage[]> {
  return api.put<ProductImage[]>(`/t/${slug}/products/${productId}/images/order`, {
    image_ids: imageIds,
  });
}

/** Batch photo upload matched by EAN file names (4848…489-1.jpg → photo #1
 *  of the product with that EAN). Returns what attached and what didn't. */
export type BulkImageResult = {
  attached: { file: string; ean: string; product_id: number; product_name: string }[];
  skipped: { file: string; reason: string; ean?: string }[];
};

export async function bulkUploadProductImages(
  slug: string,
  files: File[],
): Promise<BulkImageResult> {
  const form = new FormData();
  for (const f of files) form.append("files", f);
  const res = await fetch(`/api/v1/t/${slug}/products/images/bulk`, {
    method: "POST",
    body: form,
    credentials: "include",
  });
  if (!res.ok) {
    const body = await res.json().catch(() => null);
    throw new Error(body?.message ?? `Upload failed (${res.status})`);
  }
  return res.json();
}

/* nginx caps a request at 20 MB, and a folder of camera photos blows past
 * that in four files. Batches stay comfortably under the cap; a single file
 * larger than the whole budget is still sent alone so the server can refuse
 * it with its per-file reason instead of the transport 413-ing the batch. */
const BULK_BATCH_BYTES = 12 * 1024 * 1024;
const BULK_BATCH_FILES = 40;

/** Upload photos in EAN-named batches sized for the request limit, merging
 *  the per-file results. `onProgress(done, total)` ticks after each batch. */
export async function bulkUploadProductImagesBatched(
  slug: string,
  files: File[],
  onProgress?: (done: number, total: number) => void,
): Promise<BulkImageResult> {
  const batches: File[][] = [];
  let batch: File[] = [];
  let batchBytes = 0;
  for (const f of files) {
    if (batch.length > 0 && (batchBytes + f.size > BULK_BATCH_BYTES || batch.length >= BULK_BATCH_FILES)) {
      batches.push(batch);
      batch = [];
      batchBytes = 0;
    }
    batch.push(f);
    batchBytes += f.size;
  }
  if (batch.length > 0) batches.push(batch);

  const merged: BulkImageResult = { attached: [], skipped: [] };
  let done = 0;
  for (const b of batches) {
    // Sequential: the server derives gallery positions from existing rows,
    // and parallel batches would race each other for them.
    const res = await bulkUploadProductImages(slug, b);
    merged.attached.push(...res.attached);
    merged.skipped.push(...res.skipped);
    done += b.length;
    onProgress?.(done, files.length);
  }
  return merged;
}

/* ── Product import (CSV/XLSX upload → column mapping → run) ─────────────── */

export type ImportFieldDef = { key: string; kind: string; mandatory: boolean };
export type ImportColumn = {
  name: string;
  samples: string[];
  field: string | null;
  source: "scheme" | "guess" | null;
};
export type ImportUpload = {
  id: number;
  file_name: string;
  row_count: number;
  columns: ImportColumn[];
  scheme_id: number | null;
  scheme_name: string | null;
  fields: ImportFieldDef[];
  /** The full "complete product" preset — shown as chips. */
  mandatory_keys: string[];
  /** Hard gate: every one of these must be mapped. */
  required_keys: string[];
  /** Hard gate: at least one of these must be mapped (SKU or EAN). */
  identity_keys: string[];
  /** Warned about when unmapped, but never blocking. */
  recommended_keys: string[];
  /** No header row: the first row is a product, columns are named A, B, C… */
  headerless?: boolean;
};
export type ImportResult = {
  id: number;
  status: string;
  row_count: number;
  products_created: number;
  products_updated: number;
  products_skipped: number;
  errors: { row: number; message: string }[];
};
export type ImportListItem = {
  id: number;
  file_name: string;
  status: string;
  scheme_name: string | null;
  created_by: string | null;
  created_at: string;
  row_count: number;
  products_created: number;
  products_updated: number;
  products_skipped: number;
  error_count: number;
};

export async function uploadProductImport(slug: string, file: File): Promise<ImportUpload> {
  const form = new FormData();
  form.append("file", file);
  const res = await fetch(`/api/v1/t/${slug}/product-imports`, {
    method: "POST",
    body: form,
    credentials: "include",
  });
  if (!res.ok) {
    const body = await res.json().catch(() => null);
    throw new Error(body?.error?.message ?? "Upload failed");
  }
  return res.json();
}

export async function runProductImport(
  slug: string,
  importId: number,
  payload: {
    mapping: Record<string, string>;
    save_scheme: boolean;
    scheme_name: string;
    /** Every row's supplier where the file names none. */
    default_supplier?: string;
  },
): Promise<ImportResult> {
  return api.post<ImportResult>(`/t/${slug}/product-imports/${importId}/run`, payload);
}

export async function fetchProductImports(slug: string): Promise<ImportListItem[]> {
  return api.get<ImportListItem[]>(`/t/${slug}/product-imports`);
}

/* ── Classifiers (cross-tenant reference data, for product-card dropdowns) ── */

export type PackageCode = {
  id: number;
  code: string;
  material: string;
  abbreviation: string;
  description: string;
};
export type PalletTypeRef = {
  id: number;
  code: string;
  name: string;
  width_mm: number | null;
  length_mm: number | null;
  is_custom: boolean;
  tare_weight_min_kg: number | null;
  tare_weight_max_kg: number | null;
  /** From the company's list (Настройки → Склад → Паллеты): unticked sizes
   *  are not offered, though a card already on one keeps showing it. */
  enabled?: boolean;
};

/** «Европаллета (EUR) · 800×1200» — the name with the size when it has one. */
export function palletLabel(pt: Pick<PalletTypeRef, "name" | "width_mm" | "length_mm">): string {
  return pt.width_mm && pt.length_mm ? `${pt.name} · ${pt.width_mm}×${pt.length_mm}` : pt.name;
}

/** «Вес паллета» choices: each weight on the company's ticked list once,
 *  with the pallets counted at it. */
export function palletWeightOptions(list: PalletTypeRef[]): { value: string; label: string }[] {
  const by = new Map<string, string[]>();
  for (const pt of list) {
    if (pt.enabled === false || pt.tare_weight_max_kg == null) continue;
    const key = String(pt.tare_weight_max_kg);
    by.set(key, [...(by.get(key) ?? []), pt.name]);
  }
  return [...by.entries()]
    .sort((a, b) => Number(a[0]) - Number(b[0]))
    .map(([value, names]) => ({ value, label: `${Number(value)} kg · ${names.join(", ")}` }));
}
export type Allergen = { id: number; code: string; name: string; description: string };
export type ProductStandard = { id: number; code: string; name: string; description: string };
export type LabelIcon = { id: number; code: string; name: string; description: string };
export type CountryRef = { id: number; code: string; name: string; region: string };

/** A stored column's display unit and the factor between the two.
 *  `dp` is the stored column's decimal places — rounding to it is not cosmetic,
 *  the server validates decimal places. */
export type UnitScale = { unit: string; factor: number; dp: number };

/** How net/gross weight are shown for a product sold by `unit`.
 *
 *  The column is `*_weight_kg`, but a card sold by the gram is filled in from a
 *  label that reads "130 g" — so a card whose unit is g/ml displays and accepts
 *  grams and stores kilos. The product list has to apply the identical scale:
 *  when it printed the raw column with a fixed " kg" suffix, a 130 g sweet read
 *  as "130 kg". */
export function weightScaleForUnit(unit: string | null | undefined): UnitScale {
  const u = unit || "g";
  if (u === "g" || u === "ml") return { unit: u, factor: 0.001, dp: 3 };
  if (u === "l") return { unit: "l", factor: 1, dp: 3 };
  return { unit: "kg", factor: 1, dp: 3 };
}

/** Stored value → the number shown to the user. */
export function scaleToDisplay(v: unknown, s: UnitScale): number | "" {
  if (v === null || v === undefined || v === "") return "";
  return Number((Number(v) / s.factor).toFixed(6));
}

/** Typed number → the value stored in the column. */
export function scaleToStored(raw: string | number, s: UnitScale): number | null {
  if (raw === "" || raw === null || raw === undefined) return null;
  return Number((Number(raw) * s.factor).toFixed(s.dp));
}

/** Sort weight for a country's region, so every grouped country list orders its
 *  continents the same way: Europe first, then North America, then the rest
 *  alphabetically.
 *
 *  Regions are seeded as free text ("Западная Европа", "Северная Европа
 *  (Балтия)", …), which is why this matches on a substring rather than an exact
 *  name. Shared by Partner search and the product card's country picker — they
 *  used to carry a copy each. */
export function countryRegionRank(region: string): number {
  if (region.includes("Европа") || region.includes("Europe")) return 0;
  if (region.includes("Северная Америка") || region.includes("North America")) return 1;
  return 2;
}

export async function fetchPackageCodes(): Promise<PackageCode[]> {
  return api.get<PackageCode[]>("/meta/package-codes");
}
/** The pallets a card may pick from. With a company, its own list —
 *  Настройки → Склад → Паллеты, weights as the company counts them;
 *  without, the platform's standard sizes. */
export async function fetchPalletTypes(slug?: string): Promise<PalletTypeRef[]> {
  if (!slug) return api.get<PalletTypeRef[]>("/meta/pallet-types");
  const rows = await api.get<
    {
      id: number;
      code: string;
      name: string;
      width_mm: number | null;
      length_mm: number | null;
      weight_kg: number | null;
      enabled: boolean;
    }[]
  >(`/t/${slug}/stock/pallets`);
  return rows.map((r) => ({
    id: r.id,
    code: r.code,
    name: r.name,
    width_mm: r.width_mm,
    length_mm: r.length_mm,
    is_custom: false,
    tare_weight_min_kg: r.weight_kg,
    tare_weight_max_kg: r.weight_kg,
    enabled: r.enabled,
  }));
}
export async function fetchAllergens(): Promise<Allergen[]> {
  return api.get<Allergen[]>("/meta/allergens");
}
/** Every characteristic a card may answer, with its allowed values. Shared
 *  reference data — the same list for every company, which is what makes the
 *  catalogue filter over it mean anything. */
export type CharacteristicRef = {
  code: string;
  name: string;
  values: { id: number; code: string; name: string }[];
};
export async function fetchCharacteristics(): Promise<CharacteristicRef[]> {
  return api.get<CharacteristicRef[]>("/meta/characteristics");
}

/** An optional section of the card — «Wine» under Alcohol, «Mineral water».
 *  A card ticks it; the section then shows the characteristics it groups and
 *  the plain columns that only make sense for that kind of product. */
export type CharacteristicSetRef = {
  code: string;
  group: string | null;
  characteristics: string[];
  fields: string[];
};
export async function fetchCharacteristicSets(): Promise<CharacteristicSetRef[]> {
  return api.get<CharacteristicSetRef[]>("/meta/characteristic-sets");
}

/** Ask for a characteristic, or a value inside one, that the shared list does
 *  not have. A request, not a creation: an operator's approval is what makes
 *  it exist, and it then exists for every company. */
export type CharacteristicRequest = {
  id: number;
  kind: "characteristic" | "value";
  characteristic_code: string | null;
  characteristic_name: string | null;
  name: string;
  values: string;
  note: string;
  status: "pending" | "approved" | "rejected";
  review_note: string;
  created_at: string;
};
export async function requestCharacteristic(
  slug: string,
  body: { kind: "characteristic" | "value"; characteristic_code?: string; name: string; values?: string; note?: string },
): Promise<CharacteristicRequest> {
  return api.post<CharacteristicRequest>(`/t/${slug}/characteristic-requests`, body);
}

export async function fetchProductStandards(): Promise<ProductStandard[]> {
  return api.get<ProductStandard[]>("/meta/product-standards");
}
export async function fetchLabelIcons(): Promise<LabelIcon[]> {
  return api.get<LabelIcon[]>("/meta/label-icons");
}

/** An accounting mark the company added itself, beside deposit / sugar /
 *  alcohol / coffee. In a «Все признаки» filter it is `c<id>`. */
export type ProductMark = { id: number; name: string };
export async function fetchProductMarks(slug: string): Promise<ProductMark[]> {
  return api.get<ProductMark[]>(`/t/${slug}/product-marks`);
}
export async function createProductMark(slug: string, name: string): Promise<ProductMark> {
  return api.post<ProductMark>(`/t/${slug}/product-marks`, { name });
}
export async function deleteProductMark(slug: string, id: number): Promise<void> {
  await api.delete(`/t/${slug}/product-marks/${id}`);
}
export async function fetchCountries(): Promise<CountryRef[]> {
  return api.get<CountryRef[]>("/meta/countries");
}
